"use client";

import { useEffect, useMemo, useState } from "react";
import { attachPrincepsToGenericFamilies, classifyLgpiProduct } from "./lib/lgpi-catalog";

type CatalogPharmacy = "Square" | "Ragadan" | "Laurentine";

type CatalogRow = {
  ean: string;
  product: string;
  supplier: string;
  stock: string;
  daysToSell: string;
  lastSaleDate: string;
  lastReceptionDate: string;
  rotationFamily: string;
  genericRole: string;
  vatRate: string;
  reimbursementRate: string;
  productType: string;
  monthlySales: string;
  purchasePrice: string;
  salePrice: string;
  marketHigh: string;
  marketAverage: string;
  marketLow: string;
  marketZone: string;
};

type CatalogStat = {
  pharmacy: string;
  product_count: number;
  last_import: number;
};

type CatalogImportSuccess = {
  pharmacy: CatalogPharmacy;
  imported: number;
  integrated: number;
  fileName: string;
  completedAt: number;
};

type ArchivedCatalogItem = {
  ean?: unknown;
  pharmacy?: unknown;
  stock?: unknown;
};

const pharmacies: CatalogPharmacy[] = ["Square", "Ragadan", "Laurentine"];

const normalizeHeader = (value: string) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("fr")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const headerAliases: Record<keyof CatalogRow, string[]> = {
  ean: ["ean", "code ean", "cip", "cip 13", "code cip", "gtin", "code produit"],
  product: ["denomination", "designation", "produit", "libelle", "nom produit", "article"],
  supplier: ["fournisseur", "nom fournisseur", "laboratoire", "labo", "fabricant"],
  stock: ["stock", "stock actuel", "quantite stock", "qte stock"],
  daysToSell: ["jours ecoulement", "jours d ecoulement", "nb jours ecoulement", "rotation jours", "jours ecoul", "nbj ecoul stk"],
  lastSaleDate: ["date derniere vente", "derniere vente", "date der vente", "dern vente", "date dern vte"],
  lastReceptionDate: ["date derniere reception", "derniere reception", "date der reception", "date dern recept", "dern reception", "date derniere entree", "derniere entree"],
  rotationFamily: ["famille de rotation", "famille rotation", "famille substituable", "famille generique", "groupe generique", "classification generique", "code groupe generique"],
  genericRole: ["role generique", "statut generique", "type generique", "princeps generique", "princeps referent"],
  vatRate: ["tva", "taux tva"],
  reimbursementRate: ["taux remboursement", "remboursement", "taux remb", "remb"],
  productType: ["typologie", "type produit", "famille", "categorie", "prestation"],
  monthlySales: ["vente mensuelle", "ventes mensuelles", "moyenne mensuelle", "moy mens", "moy vte mens", "rotation mensuelle"],
  purchasePrice: ["pamp net", "prix achat", "prix d achat"],
  salePrice: ["pv ttc", "prix vente ttc", "prix de vente"],
  marketHigh: ["pmc haut"],
  marketAverage: ["pmc moyen"],
  marketLow: ["pmc bas"],
  marketZone: ["pmc zgp"],
};

function delimiterScore(line: string, delimiter: string) {
  let score = 0;
  let quoted = false;
  for (const char of line) {
    if (char === "\"") quoted = !quoted;
    if (!quoted && char === delimiter) score += 1;
  }
  return score;
}

function parseDelimited(text: string, delimiter: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === "\"") {
      if (quoted && text[index + 1] === "\"") {
        cell += "\"";
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
}

function parseCatalogFile(text: string) {
  const firstLine = text.split(/\r?\n/).find((line) => line.trim()) ?? "";
  const delimiter = [";", "\t", ","].sort((a, b) => delimiterScore(firstLine, b) - delimiterScore(firstLine, a))[0];
  const matrix = parseDelimited(text.replace(/^\uFEFF/, ""), delimiter);
  if (!matrix.length) return { rows: [] as CatalogRow[], rejected: 0, delimiter };

  const headerRowIndex = matrix.slice(0, 25).reduce((bestIndex, row, index) => {
    const headers = row.map(normalizeHeader);
    const score = Object.values(headerAliases).filter((aliases) => headers.some((header) => aliases.some((alias) => header === alias || header.includes(alias)))).length;
    const bestHeaders = matrix[bestIndex].map(normalizeHeader);
    const bestScore = Object.values(headerAliases).filter((aliases) => bestHeaders.some((header) => aliases.some((alias) => header === alias || header.includes(alias)))).length;
    return score > bestScore ? index : bestIndex;
  }, 0);
  const normalizedHeaders = matrix[headerRowIndex].map(normalizeHeader);
  const findHeaderIndex = (aliases: string[]) => {
    const exactIndex = normalizedHeaders.findIndex((header) => aliases.includes(header));
    if (exactIndex >= 0) return exactIndex;
    return normalizedHeaders.findIndex((header) => aliases.some((alias) => alias.length >= 5 && header.includes(alias)));
  };
  const indexes = Object.fromEntries(
    Object.entries(headerAliases).map(([field, aliases]) => [
      field,
      findHeaderIndex(aliases),
    ]),
  ) as Record<keyof CatalogRow, number>;
  // « Code produit » est un EAN dans certains exports LGPI : il ne doit
  // jamais être réutilisé comme dénomination à cause du mot « produit ».
  if (indexes.product === indexes.ean) {
    indexes.product = normalizedHeaders.findIndex((header, index) => index !== indexes.ean && [
      "denomination", "designation", "libelle", "nom produit", "article",
    ].some((alias) => header === alias || header.includes(alias)));
  }
  const hasHeader = indexes.ean >= 0 || indexes.product >= 0;
  const dataRows = hasHeader ? matrix.slice(headerRowIndex + 1) : matrix;
  if (indexes.ean < 0) indexes.ean = 0;
  if (indexes.product < 0 && !hasHeader) indexes.product = 1;
  // Dans l’export catalogue LGPI utilisé par les officines, Fabricant est la colonne J.
  if (indexes.supplier < 0 && matrix.some((row) => row.length >= 10)) indexes.supplier = 9;
  const prestationIndex = normalizedHeaders.findIndex((header) => header === "prestation" || header.includes("prestation"));
  const lgpiPrestationIndex = prestationIndex >= 0 ? prestationIndex : matrix.some((row) => row.length >= 4) ? 3 : -1;
  const lgpiGenericIndex = indexes.rotationFamily >= 0
    ? indexes.rotationFamily
    : !hasHeader && matrix.some((row) => row.length >= 3) ? 2 : -1;

  let rejected = 0;
  const byEan = new Map<string, CatalogRow>();
  dataRows.forEach((cells) => {
    const read = (field: keyof CatalogRow) => indexes[field] >= 0 ? String(cells[indexes[field]] ?? "").trim() : "";
    const ean = read("ean").replace(/\D/g, "");
    const product = read("product");
    if (ean.length < 8 || !product || product.replace(/\D/g, "") === ean || !/[a-zà-ÿ]/i.test(product)) {
      rejected += 1;
      return;
    }
    const prestation = lgpiPrestationIndex >= 0 ? String(cells[lgpiPrestationIndex] ?? "").trim() : "";
    const genericMarker = lgpiGenericIndex >= 0 ? String(cells[lgpiGenericIndex] ?? "").trim() : "";
    const classification = classifyLgpiProduct({
      product,
      prestation,
      genericMarker,
      explicitFamily: read("rotationFamily"),
      explicitRole: read("genericRole"),
      explicitType: read("productType"),
    });
    byEan.set(ean, {
      ean,
      product,
      supplier: read("supplier"),
      stock: read("stock"),
      daysToSell: read("daysToSell"),
      lastSaleDate: read("lastSaleDate"),
      lastReceptionDate: read("lastReceptionDate"),
      rotationFamily: classification.rotationFamily,
      genericRole: classification.genericRole,
      vatRate: read("vatRate"),
      reimbursementRate: read("reimbursementRate"),
      productType: classification.productType,
      monthlySales: read("monthlySales"),
      purchasePrice: read("purchasePrice"),
      salePrice: read("salePrice"),
      marketHigh: read("marketHigh"),
      marketAverage: read("marketAverage"),
      marketLow: read("marketLow"),
      marketZone: read("marketZone"),
    });
  });
  return { rows: attachPrincepsToGenericFamilies([...byEan.values()]), rejected, delimiter };
}

function formatMoment(value: number) {
  if (!value) return "Jamais importé";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function stockLookupPrefixes(rows: CatalogRow[], maximumPerPrefix = 4000) {
  const missing = rows.filter((row) => !String(row.stock || "").trim());
  const split = (candidates: CatalogRow[], length: number): string[] => {
    const groups = new Map<string, CatalogRow[]>();
    for (const row of candidates) {
      const prefix = row.ean.slice(0, length);
      groups.set(prefix, [...(groups.get(prefix) ?? []), row]);
    }
    return [...groups.entries()].flatMap(([prefix, group]) =>
      group.length > maximumPerPrefix && length < 8 ? split(group, length + 1) : [prefix]);
  };
  return split(missing, 2);
}

export function AdminCatalogImport() {
  const [pharmacy, setPharmacy] = useState<CatalogPharmacy>("Square");
  const [stats, setStats] = useState<CatalogStat[]>([]);
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [fileName, setFileName] = useState("");
  const [rejected, setRejected] = useState(0);
  const [replacePharmacy, setReplacePharmacy] = useState(true);
  const [progress, setProgress] = useState(0);
  const [imported, setImported] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState<CatalogImportSuccess | null>(null);

  const loadStats = async () => {
    try {
      const response = await fetch("/api/product-catalog?storage=import&limit=500", { cache: "no-store" });
      const payload = await response.json() as { stats?: CatalogStat[] };
      if (response.ok) {
        const nextStats = payload.stats ?? [];
        setStats(nextStats);
        return nextStats;
      }
    } catch {
      setMessage("Les compteurs de catalogue ne sont pas disponibles pour le moment.");
    }
    return [];
  };

  useEffect(() => {
    void loadStats();
  }, []);

  const totalProducts = useMemo(
    () => stats.reduce((sum, item) => sum + Number(item.product_count || 0), 0),
    [stats],
  );

  const pickFile = async (file?: File) => {
    setMessage("");
    setSuccess(null);
    setRows([]);
    setRejected(0);
    setFileName(file?.name ?? "");
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setMessage("Fichier trop volumineux : exportez-le en CSV ou TXT de moins de 25 Mo.");
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const utf8 = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
      const text = utf8.includes("\uFFFD")
        ? new TextDecoder("windows-1252").decode(buffer)
        : utf8;
      const parsed = parseCatalogFile(text);
      setRows(parsed.rows);
      setRejected(parsed.rejected);
      setMessage(parsed.rows.length
        ? `${parsed.rows.length.toLocaleString("fr-FR")} produits reconnus. Le fichier est prêt.`
        : "Aucune ligne reconnue. Vérifiez les colonnes EAN/CIP et dénomination.");
    } catch {
      setMessage("Le fichier n’a pas pu être lu. Utilisez un export CSV, TSV ou TXT.");
    }
  };

  const importCatalog = async () => {
    if (!rows.length || busy) return;
    setBusy(true);
    setImported(0);
    setProgress(0);
    setMessage("");
    setSuccess(null);
    const chunkSize = 500;
    let importedCount = 0;
    try {
      let importRows = rows;
      const rowsWithoutStock = rows.filter((row) => !String(row.stock || "").trim());
      if (rowsWithoutStock.length) {
        setMessage("Récupération des stocks déjà connus avant la mise à jour…");
        const expectedEans = new Set(rowsWithoutStock.map((row) => row.ean));
        const stockByEan = new Map<string, string>();
        for (const prefix of stockLookupPrefixes(rowsWithoutStock)) {
          const params = new URLSearchParams({
            archiveStock: "1",
            pharmacy,
            search: prefix,
            limit: "5000",
          });
          // URL absolue : cette lecture volumineuse contourne volontairement le
          // bridge des recherches interactives (borné à 100) et appelle le
          // Worker Pages, seul endpoint autorisé à renvoyer 5 000 stocks archivés.
          const response = await fetch(`${window.location.origin}/api/product-catalog?${params}`, { cache: "no-store" });
          const payload = await response.json() as { items?: ArchivedCatalogItem[]; error?: string };
          if (!response.ok) throw new Error(payload.error || "Les stocks existants n’ont pas pu être récupérés.");
          for (const item of payload.items ?? []) {
            const ean = String(item.ean || "").replace(/\D/g, "");
            if (String(item.pharmacy || "") !== pharmacy || !expectedEans.has(ean)) continue;
            const stock = Number(item.stock);
            if (Number.isFinite(stock)) stockByEan.set(ean, String(stock));
          }
        }
        if (!stockByEan.size) {
          throw new Error("Le fichier ne contient pas de colonne stock et aucun stock antérieur n’a été retrouvé. Import annulé sans remplacer le catalogue.");
        }
        importRows = rows.map((row) => String(row.stock || "").trim() || !stockByEan.has(row.ean)
          ? row
          : { ...row, stock: stockByEan.get(row.ean) ?? "" });
        setMessage(`${stockByEan.size.toLocaleString("fr-FR")} stocks existants récupérés. Import en cours…`);
      }
      for (let start = 0; start < importRows.length; start += chunkSize) {
        const chunk = importRows.slice(start, start + chunkSize);
        let payload: { imported?: number; error?: string } = {};
        let completed = false;
        for (let attempt = 0; attempt < 3 && !completed; attempt += 1) {
          try {
            const response = await fetch("/api/product-catalog", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                pharmacy,
                rows: chunk,
                replacePharmacy: replacePharmacy && start === 0,
                source: `LGPI · ${fileName}`,
              }),
            });
            payload = await response.json() as { imported?: number; error?: string };
            if (response.ok) completed = true;
            else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
              const rejected = new Error(payload.error || "Import refusé.");
              rejected.name = "CatalogImportRejected";
              throw rejected;
            }
            else if (attempt === 2) throw new Error(payload.error || "Import interrompu après trois tentatives.");
          } catch (error) {
            if (attempt === 2 || error instanceof Error && error.name === "CatalogImportRejected") throw error;
          }
          if (!completed) await new Promise((resolve) => window.setTimeout(resolve, 800 * (attempt + 1)));
        }
        if (!completed) throw new Error("Import interrompu après trois tentatives.");
        importedCount += Number(payload.imported ?? 0);
        setImported(importedCount);
        setProgress(Math.round(Math.min(importRows.length, start + chunk.length) / importRows.length * 100));
      }
      const refreshedStats = await loadStats();
      const verificationResponse = await fetch(`/api/product-catalog?storage=import&pharmacy=${encodeURIComponent(pharmacy)}&limit=500&verifiedAt=${Date.now()}`, { cache: "no-store" });
      const verification = await verificationResponse.json() as { items?: unknown[]; stats?: CatalogStat[]; error?: string };
      if (!verificationResponse.ok) throw new Error(verification.error || "Import terminé, mais vérification de l’intégration impossible.");
      const verifiedStats = verification.stats?.length ? verification.stats : refreshedStats;
      const integrated = Number(verifiedStats.find((item) => item.pharmacy === pharmacy)?.product_count ?? 0);
      if (!integrated && !(verification.items ?? []).length) throw new Error("Import terminé, mais aucun produit intégré n’a été retrouvé.");
      setSuccess({ pharmacy, imported: importedCount, integrated: integrated || importedCount, fileName, completedAt: Date.now() });
      setMessage("");
    } catch (error) {
      setMessage(error instanceof Error ? `${error.message} ${importedCount.toLocaleString("fr-FR")} lignes avaient déjà été traitées.` : "L’import a été interrompu.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin-content-card admin-catalog">
      <header>
        <div><span>DONNÉES PRODUITS</span><h1>Catalogues LGPI</h1><p>Un fichier séparé par pharmacie, mis à jour sans ralentir le travail au comptoir.</p></div>
        <b className="admin-catalog-total">{totalProducts.toLocaleString("fr-FR")} produits</b>
      </header>

      <div className="admin-catalog-stats">
        {pharmacies.map((name, index) => {
          const stat = stats.find((item) => item.pharmacy === name);
          return (
            <article key={name} className={name.toLocaleLowerCase("fr")}>
              <span>{["🏥", "🌿", "✨"][index]}</span>
              <div><strong>{name}</strong><small>{formatMoment(Number(stat?.last_import ?? 0))}</small></div>
              <b>{Number(stat?.product_count ?? 0).toLocaleString("fr-FR")}</b>
            </article>
          );
        })}
      </div>

      <div className="admin-catalog-grid">
        <section className="admin-catalog-importer">
          <div className="admin-catalog-title"><span>🏷️</span><div><h2>Importer ou mettre à jour</h2><p>CSV, TSV ou TXT exporté depuis LGPI.</p></div></div>
          <div className="admin-catalog-steps">
            <label><span>1</span><b>Pharmacie</b>
              <select value={pharmacy} onChange={(event) => setPharmacy(event.target.value as CatalogPharmacy)}>
                {pharmacies.map((name) => <option key={name}>{name}</option>)}
              </select>
            </label>
            <label><span>2</span><b>Fichier catalogue</b>
              <input type="file" accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain" onChange={(event) => void pickFile(event.target.files?.[0])} />
            </label>
          </div>
          <label className="admin-catalog-mode">
            <input type="checkbox" checked={replacePharmacy} onChange={(event) => setReplacePharmacy(event.target.checked)} />
            <span><b>Remplacer le catalogue de {pharmacy}</b><small>Conseillé pour l’import hebdomadaire complet. Les autres pharmacies restent intactes.</small></span>
          </label>
          {fileName && (
            <div className="admin-catalog-file">
              <span>📄</span>
              <div><strong>{fileName}</strong><small>{rows.length.toLocaleString("fr-FR")} lignes valides{rejected ? ` · ${rejected.toLocaleString("fr-FR")} ignorées` : ""}</small></div>
              <b>{busy ? `${progress}%` : "Prêt"}</b>
            </div>
          )}
          {busy && <div className="admin-catalog-progress"><i style={{ width: `${progress}%` }} /></div>}
          <button type="button" className="admin-catalog-submit" disabled={!rows.length || busy} onClick={() => void importCatalog()}>
            {busy ? `Import en cours · ${imported.toLocaleString("fr-FR")} / ${rows.length.toLocaleString("fr-FR")}` : `⬆ Importer pour ${pharmacy}`}
          </button>
          {success && <div className="admin-catalog-success" role="status"><span>✓</span><div><strong>Import et intégration réussis</strong><p><b>{success.imported.toLocaleString("fr-FR")}</b> lignes du fichier « {success.fileName} » ont été traitées pour {success.pharmacy}. Le catalogue actif contient maintenant <b>{success.integrated.toLocaleString("fr-FR")}</b> produits.</p><small>Vérification terminée le {formatMoment(success.completedAt)} · les modules Prix, stocks dormants et PharmaScan peuvent utiliser ces données.</small></div></div>}
          {message && <p className="admin-catalog-message">{message}</p>}
        </section>

        <aside className="admin-catalog-explain">
          <span>⚡</span>
          <h2>Pas trop lourd ? Non.</h2>
          <p>Le fichier est lu et contrôlé directement sur ce poste, puis envoyé au portail par lots de 500 produits.</p>
          <ul>
            <li><b>Une pharmacie à la fois</b><span>Aucun mélange entre Square, Ragadan et Laurentine.</span></li>
            <li><b>Portail toujours disponible</b><span>L’équipe peut continuer à travailler pendant l’import.</span></li>
            <li><b>Mise à jour propre</b><span>EAN, stock, rotation et dernière vente alimentent ensuite tous les modules utiles.</span></li>
          </ul>
        </aside>
      </div>
    </section>
  );
}
